import axios from "axios";
import { baseUrl } from "./config";

const api = axios.create({
  baseURL: `${baseUrl}`,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (cb) => {
  refreshSubscribers.push(cb);
};

const onRefreshed = (token) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

const forceLogout = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("refresh_token");
  window.location.href = "/login";
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;

    // If no response or not unauthorized, just reject
    if (!response || response.status !== 401) {
      return Promise.reject(error);
    }

    // Avoid infinite loop
    if (config._retry) {
      forceLogout();
      return Promise.reject(error);
    }
    config._retry = true;

    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) {
      forceLogout();
      return Promise.reject(error);
    }

    // If already refreshing, queue the request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        subscribeTokenRefresh((newToken) => {
          if (!newToken) {
            reject(error);
            return;
          }
          config.headers.Authorization = `Bearer ${newToken}`;
          resolve(api(config));
        });
      });
    }

    isRefreshing = true;

    try {
      // Call refresh endpoint with refresh token
      const refreshResponse = await axios.post(
        `${baseUrl}/auth/refresh`,
        {},
        {
          headers: {
            Authorization: `Bearer ${refreshToken}`,
          },
        }
      );

      const newAccessToken = refreshResponse.data.access_token;
      if (!newAccessToken) {
        forceLogout();
        return Promise.reject(error);
      }

      localStorage.setItem("token", newAccessToken);
      api.defaults.headers.Authorization = `Bearer ${newAccessToken}`;
      onRefreshed(newAccessToken);

      config.headers.Authorization = `Bearer ${newAccessToken}`;
      return api(config);
    } catch (err) {
      onRefreshed(null);
      forceLogout();
      return Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  }
);

export default api;
